import {useCallback, useEffect, useMemo} from 'react';

import {IMPORT_PANEL_QS} from '../../components/ReportPanelGridSection';
import {
  GALLERY_POST_EDIT_PATH,
  REPORT_PAGE_EDIT_PATH,
  REPORT_PAGE_VIEW_PATH,
} from '../../routes/paths';
import * as Analytics from '../../services/analytics';
import {auth} from '../../setup';
import {useDispatch, usePropsSelector} from '../../state/hooks';
import * as PollingActions from '../../state/polling/actions';
import * as ReportActions from '../../state/reports/actions';
import * as ReportThunks from '../../state/reports/thunks';
import {ReportViewRef} from '../../state/reports/types';
import {Viewer} from '../../state/viewer/types';
import * as ViewHooks from '../../state/views/hooks';
import * as ViewSelectors from '../../state/views/selectors';
import * as ViewThunks from '../../state/views/thunks';
import {LoadableView} from '../../state/views/types';
import {
  maybeLengthenReportDescription,
  setDocumentDescription,
  setDocumentTitle,
} from '../../util/document';
import {keyExistsInQueryString} from '../../util/query-string';
import {ReportViewInfo} from './selectors';
import {ReportMode} from './types';

export function useLoadReport(reportId: string) {
  const dispatch = useDispatch();

  // Starts the process of loading the report info into "reports" and "views" reducers
  // Clean up of views.views happens in report thunk
  useEffect(() => {
    const loadStartAction = ReportActions.loadStarted(reportId);
    dispatch(ReportThunks.load(loadStartAction));
  }, [dispatch, reportId]);
}

export function useReportMode(path: string): ReportMode {
  const reportMode = useMemo(() => {
    if (path === REPORT_PAGE_EDIT_PATH || path === GALLERY_POST_EDIT_PATH) {
      return ReportMode.IS_EDITING;
    }

    if (path === REPORT_PAGE_VIEW_PATH) {
      return ReportMode.IS_VIEWING;
    }

    return ReportMode.IS_VIEWING; // Default to viewing for now
  }, [path]);

  return reportMode;
}

export function useReportModeSideEffects({
  reportMode,
  entityName,
  projectName,
}: {
  reportMode: ReportMode;
  reportViewInfo: ReportViewInfo;
  entityName?: string;
  projectName?: string;
}) {
  const dispatch = useDispatch();

  // Used to track if import panel is open when user goes to a report draft
  useEffect(() => {
    if (
      reportMode === ReportMode.IS_EDITING &&
      keyExistsInQueryString(IMPORT_PANEL_QS)
    ) {
      Analytics.editReportStartedWithImportPanelOpen({
        entityName,
        projectName,
      });
    }
  }, [entityName, projectName, reportMode]);

  useEffect(() => {
    // Don't poll on the report page for now
    dispatch(PollingActions.setBasePollInterval(0));
  }, [dispatch]);
}

export function useSetReportTitleAndDescription(view?: LoadableView) {
  useEffect(() => {
    setDocumentTitle(
      `${view?.displayName}${
        view?.project == null ? '' : ` | ${view?.project.name}`
      }`
    );
    setDocumentDescription(
      maybeLengthenReportDescription(
        view?.description ?? '',
        view?.user ?? {username: '', name: ''}
      )
    );
  }, [view]);
}

export function useReportStar({
  reportId,
  viewer,
  reportViewRef,
  isPublicProject,
}: {
  reportId: string;
  viewer?: Viewer;
  reportViewRef: ReportViewRef;
  isPublicProject: boolean;
}) {
  const starCount = usePropsSelector(
    ViewSelectors.makeStarCountSelector,
    reportViewRef
  );
  const starred = usePropsSelector(
    ViewSelectors.makeStarredSelector,
    reportViewRef
  );
  const star = ViewHooks.useViewRefThunk(reportViewRef, ViewThunks.starView);
  const unstar = ViewHooks.useViewRefThunk(
    reportViewRef,
    ViewThunks.unstarView
  );

  const toggleStar = useCallback(() => {
    if (viewer == null) {
      auth.login();
      return;
    }

    Analytics.reportHeartClicked({
      isPublicProject,
      reportId,
      actionTriggered: starred ? 'unheart' : 'heart',
    });

    if (starred) {
      unstar();
    } else {
      star();
    }
  }, [viewer, isPublicProject, reportId, starred, unstar, star]);

  return {starCount, starred, toggleStar};
}
